import { useRef, useState } from "react";
import { useToast } from "@/components/ui/Toast";
import { WhatsAppIcon } from "@/components/ui/WhatsAppIcon";
import { api } from "@/lib/api";
import { WA_TEMPLATE_LABEL, phoneUsable } from "@/lib/brief";
import { cn } from "@/lib/utils";
import { sendConfirmationToExtension, waExtensionReady } from "@/lib/wa-extension";
import type { Prospect } from "@shared/schema";
import { renderWhatsApp, waLink, type WaTemplate } from "@shared/whatsapp";

const NO_PHONE_TITLE = "Pas de numéro utilisable";

/** Message du gabarit ; vide si le rendu échoue (date illisible) : le texte reste modifiable. */
export function draftFor(
  prospect: Pick<Prospect, "firstName">,
  template: WaTemplate,
  rdvStart: string | undefined,
  bookingUrl: string,
  bookedAt?: string,
): string {
  try {
    return renderWhatsApp(template, { firstName: prospect.firstName, rdvStart, bookedAt, bookingUrl });
  } catch {
    return "";
  }
}

/**
 * Bouton WhatsApp en un clic : ouvre `wa.me` avec le message du gabarit dans un nouvel onglet
 * (le texte reste modifiable dans WhatsApp) et journalise une interaction `message`.
 * Désactivé sans numéro utilisable (`waLink` renvoie null).
 */
export function WhatsAppButton({
  prospect,
  template,
  rdvStart,
  bookedAt,
  bookingUrl,
  size = "md",
  variant = "default",
  label,
  className,
}: {
  prospect: Prospect;
  template: WaTemplate;
  rdvStart?: string;
  /** ISO de la réservation (`bookedAtOf(event.rawJson)`), pour le gabarit confirmation. */
  bookedAt?: string;
  bookingUrl: string;
  size?: "sm" | "md";
  /** `round` : rond 44 px ; `icon` : rond 36 px (44 px sous 640 px) ; `solid` : fond vert plein. */
  variant?: "default" | "round" | "solid" | "icon";
  label?: string;
  className?: string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const usable = phoneUsable(prospect.phone);
  const text = label ?? "WhatsApp";

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    const draft = draftFor(prospect, template, rdvStart, bookingUrl, bookedAt);
    const url = waLink(prospect.phone, draft);
    if (!url) {
      toast.error(NO_PHONE_TITLE);
      inFlight.current = false;
      return;
    }
    // Avec l'extension, la confirmation passe par WhatsApp Web (contact et liste en plus). Sinon, wa.me.
    const viaExtension =
      template === "confirmation" && waExtensionReady() && sendConfirmationToExtension(prospect, draft);
    if (!viaExtension) {
      // Ouvrir avant tout await : sinon Safari bloque la fenêtre.
      const w = window.open(url, "_blank");
      if (w) w.opener = null;
      else {
        toast.error("Le navigateur a bloqué l'ouverture de WhatsApp. Autorise les fenêtres pour ce site.");
        inFlight.current = false;
        return;
      }
    }
    setBusy(true);
    try {
      await api.createInteraction({
        prospectId: prospect.id,
        kind: "message",
        body: `WhatsApp ouvert : ${WA_TEMPLATE_LABEL[template]}`,
      });
      toast.success("WhatsApp ouvert, c'est noté dans la timeline");
    } catch (err) {
      toast.error(
        `WhatsApp est ouvert mais la note n'a pas pu être enregistrée : ${
          err instanceof Error ? err.message : "erreur"
        }`,
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  const common = {
    type: "button" as const,
    onClick,
    disabled: !usable || busy,
    title: usable ? undefined : NO_PHONE_TITLE,
  };

  if (variant === "round" || variant === "icon") {
    return (
      <button
        {...common}
        aria-label={text}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full border border-[#86efac] bg-[var(--color-surface)] text-[#16a34a] transition-colors hover:bg-[#f0fdf4] disabled:cursor-not-allowed disabled:opacity-50",
          variant === "round" ? "h-11 w-11" : "h-11 w-11 sm:h-9 sm:w-9",
          className,
        )}
      >
        <WhatsAppIcon size={20} />
      </button>
    );
  }
  return (
    <button
      {...common}
      className={cn(
        "btn",
        variant === "solid" ? "btn-success" : "btn-success-outline",
        size === "sm" && "btn-sm",
        className,
      )}
    >
      <WhatsAppIcon size={size === "sm" ? 14 : 16} />
      {text}
    </button>
  );
}
